import CustomPaper from '../common/CustomPaper';
import React, { useState, useEffect, useMemo, useContext } from 'react';
import W86PlaceholderTextField from '../W86Components/W86PlaceholderTextField';
import { send_component_save_request, send_request } from '../../utils/Request';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import { getCurrentTime } from '../../utils/getCurrentTime';
import useConvergeThreadsStore, { getConvergeThreadsStore } from './useConvergeThreadsStore';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import { computeAllPlaceholdersMap } from '../../utils/placeholderExtractor';
import { CanvasContext } from '../pages/Canvas/WorkflowCanvas';
import './ConvergeThreadsStyle.css';

const ConvergeThreadsTemplate = (props) => {
  const [loading, setLoading] = useState(true);
  const [threadPlaceholderKeys, setThreadPlaceholderKeys] = useState([]);

  const store = useMemo(() => getConvergeThreadsStore(props.component_id), [props.component_id]);

  const data = store((s) => s.data);

  const canvasContext = useContext(CanvasContext) || {};
  const contextPlaceholderMap = canvasContext.allPlaceholdersInWorkflow || {};

  const getThreadComponentIds = (convergeComponentId) => {
    const components = useProjectStore.getState().components || [];
    if (components.length === 0) return [];

    const nextMap = new Map();
    components.forEach((component) => {
      const nextIds = (component.nextComponents || [])
        .map((next) => next?.componentId)
        .filter(Boolean);
      nextMap.set(component.componentId, nextIds);
    });

    const loopComponents = components.filter((component) => component.type === 'loop_through_list');

    for (const loopComponent of loopComponents) {
      const visited = new Set();
      const region = new Set();
      const queue = [loopComponent.componentId];

      while (queue.length > 0) {
        const currentId = queue.shift();
        if (visited.has(currentId)) continue;
        visited.add(currentId);

        if (currentId === convergeComponentId) {
          return Array.from(region);
        }

        region.add(currentId);
        const nextIds = nextMap.get(currentId) || [];
        nextIds.forEach((nextId) => {
          if (!visited.has(nextId)) {
            queue.push(nextId);
          }
        });
      }
    }

    return [];
  };

  const loadThreadPlaceholderKeys = () => {
    const { component_id } = props;
    if (!component_id) return;

    const components = useProjectStore.getState().components;
    const placeholderMap =
      contextPlaceholderMap && Object.keys(contextPlaceholderMap).length > 0
        ? contextPlaceholderMap
        : computeAllPlaceholdersMap(components);
    const threadComponentIds = getThreadComponentIds(component_id);

    const collectedKeys = [];
    threadComponentIds.forEach((componentId) => {
      const placeholders = placeholderMap[componentId] || [];
      placeholders.forEach((placeholder) => {
        if (placeholder?.key) {
          collectedKeys.push(placeholder.key);
        }
      });
    });

    const uniqueKeys = Array.from(new Set(collectedKeys)).filter(
      (key) => !key.startsWith('looplist')
    );

    setThreadPlaceholderKeys(uniqueKeys);
  };

  useEffect(() => {
    if (!props.component_id) return;

    // If store was prefilled from canvas load, skip the REST fetch
    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      props.onLastModifiedChanged(store.getState().lastModified);
      loadThreadPlaceholderKeys();
      setLoading(false);
      props.setChildTemplateLoaded(true);
      return;
    }

    const load = async () => {
      try {
        const response = await send_request(
          `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
          '',
          {}
        );
        const component = response?.data?.components?.[0] || response?.data;
        if (component) {
          store.getState().setComponentData(component);
          store.setState({ loaded: true });
          props.onComponentNameChanged(store.getState().name);
          props.onLastModifiedChanged(store.getState().lastModified);
        }
        loadThreadPlaceholderKeys();
      } finally {
        setLoading(false);
        props.setChildTemplateLoaded(true);
      }
    };

    load();
  }, []);

  // Re-resolve thread placeholder keys when the canonical map arrives.
  useEffect(() => {
    if (!props.component_id) return;
    loadThreadPlaceholderKeys();
  }, [contextPlaceholderMap]);

  const getOutputPreview = () => {
    const placeholderKey = data.outputKey || 'converged_output';
    const { availablePlaceholders } = props;
    const splitPlaceholder =
      availablePlaceholders?.[0]?.placeholders?.find((p) => p.key?.startsWith('looplist')) || null;

    const inputItemValue = splitPlaceholder
      ? `\${${splitPlaceholder.key}}`
      : '${looplist_item}';

    const outputValue = {};
    threadPlaceholderKeys.forEach((key) => {
      outputValue[key] = `\${${key}}`;
    });

    return [
      {
        threadId: 'thread_abc123',
        inputItem: inputItemValue,
        output: outputValue,
      },
      {
        threadId: 'thread_def456',
        inputItem: inputItemValue,
        output: outputValue,
      },
    ];
  };

  if (loading) return null;

  return (
    <CustomPaper className="converge-threads-container" title="Configuration">
      <div className="converge-threads-field">
        <W86PlaceholderTextField
          value={data.outputKey}
          label="Output Placeholder Name"
          readOnly={props.readOnly}
          handleChange={(e) =>
            store.getState().setOutputPlaceholderKey(e.target.value)
          }
        />
      </div>
      <div className="converge-threads-info">
        Must be downstream of Split into Threads. All threads must complete before continuing.
      </div>
      <div className="converge-threads-preview">
        <div className="converge-threads-preview-label">Output Format Preview</div>
        <pre className="converge-threads-preview-code">
          {JSON.stringify(getOutputPreview(), null, 2)}
        </pre>
        <div className="converge-threads-preview-help">
          <strong>threadId</strong>: Unique identifier for each thread<br />
          <strong>inputItem</strong>: The item from the split list that triggered this thread<br />
          <strong>output</strong>: Exported placeholders from components inside the thread
        </div>
      </div>
    </CustomPaper>
  );
};

export default ConvergeThreadsTemplate;
